import { MessageSegment, ConfirmationBlock, QuestionBlock, WorkflowPreviewBlock } from './types';

const JSON_BLOCK_REGEX = /```json:(\w+)\n([\s\S]*?)```/g;

export default function parseMessageSegments(content: string): MessageSegment[] {
  const segments: MessageSegment[] = [];
  let lastIndex = 0;

  const regex = new RegExp(JSON_BLOCK_REGEX.source, 'g');
  let match: RegExpExecArray | null;

  while ((match = regex.exec(content)) !== null) {
    const matchStart = match.index;
    const matchEnd = matchStart + match[0].length;
    const blockType = match[1];
    const jsonStr = match[2].trim();

    if (matchStart > lastIndex) {
      const text = content.slice(lastIndex, matchStart).trim();
      if (text) {
        segments.push({ kind: 'text', content: text });
      }
    }

    try {
      const parsed = JSON.parse(jsonStr);

      if (blockType === 'confirmation') {
        const block: ConfirmationBlock = {
          type: 'confirmation',
          field: parsed.field || '',
          value: parsed.value || '',
          status: parsed.status || 'confirmed',
        };
        segments.push({ kind: 'confirmation', data: block });
      } else if (blockType === 'question') {
        const block: QuestionBlock = {
          type: 'question',
          id: parsed.id || '',
          questionType: parsed.type || 'free_text',
          prompt: parsed.prompt || '',
          options: parsed.options || [],
        };
        segments.push({ kind: 'question', data: block });
      } else if (blockType === 'workflow_preview') {
        const block: WorkflowPreviewBlock = {
          type: 'workflow_preview',
          step: parsed.step ?? 0,
          progress: parsed.progress || {
            event: 'pending',
            recipient: 'pending',
            notification: 'pending',
            escalation: 'pending',
          },
          nodes: Array.isArray(parsed.nodes) ? parsed.nodes : [],
          triggerJson: parsed.trigger_json || null,
          workflowJson: parsed.workflow_json || null,
        };
        segments.push({ kind: 'workflow_preview', data: block });
      } else {
        segments.push({ kind: 'text', content: match[0] });
      }
    } catch {
      segments.push({ kind: 'text', content: match[0] });
    }

    lastIndex = matchEnd;
  }

  if (lastIndex < content.length) {
    let remaining = content.slice(lastIndex).trim();
    if (remaining) {
      // Strip any incomplete fenced block at the end (streaming).
      // An odd total count of ``` means the last opened block has no closing fence.
      const backtickCount = (remaining.match(/```/g) || []).length;
      if (backtickCount % 2 !== 0) {
        const incompleteIdx = remaining.lastIndexOf('```');
        remaining = remaining.slice(0, incompleteIdx).trim();
      }
      if (remaining) {
        segments.push({ kind: 'text', content: remaining });
      }
    }
  }

  return segments;
}
